import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Mail, ArrowLeft, ShieldAlert, Lock, Check, AlertCircle } from "lucide-react";
import Logo from "@/components/Logo";

export default function ResetPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ kind: "", text: "" });

  const submit = async (e) => {
    e.preventDefault();
    setMsg({ kind: "", text: "" });
    if (!email.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/forgot-password", { email: email.trim() });
      setMsg({
        kind: "success",
        text: r.data?.message || "If an account exists for that email, a new password has been sent.",
      });
      setEmail("");
    } catch (err) {
      // Backend always returns 200 for anti-enumeration; treat anything else as a transient error.
      setMsg({
        kind: "error",
        text: err.response?.data?.detail || "Couldn't process your request right now. Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 bg-stone-50" data-testid="reset-password-page">
      {/* Left: hero panel (mirrors the login hero) */}
      <div className="hidden lg:flex relative overflow-hidden hero-gradient">
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="text-white">
            <Logo size={40} withWordmark subtitle="Chemotherapy Treatment Companion" />
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight">Reset your password</h1>
            <p className="mt-4 text-brand-100 text-lg leading-relaxed">
              Forgot your password? No problem. Enter the email tied to your account and we'll send you a fresh
              temporary password so you can get back into OncoCadence in seconds.
            </p>
            <div className="mt-8 flex items-start gap-3 text-sm text-brand-100">
              <Lock className="w-5 h-5 mt-0.5 shrink-0" />
              <span>For your security, please sign in with the temporary password and change it right away from
              <i> Profile → Change Password</i>.</span>
            </div>
          </div>
          <div className="text-xs text-brand-200">© OncoCadence — secure provider &amp; patient portal</div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-brand-700">
            <Logo size={36} withWordmark />
          </div>

          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-display text-3xl font-bold text-stone-900">Reset Password</h2>
              <p className="text-stone-500 mt-2">
                Enter your email address and we'll send you a new password.
              </p>
            </div>
            <Link
              to="/login"
              data-testid="reset-back-link"
              className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 font-medium shrink-0 mt-2"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">Email Address</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  data-testid="reset-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@hospital.com"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-stone-300 bg-white text-stone-900 placeholder-stone-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
              </div>
            </div>

            {msg.text && (
              <div
                data-testid={`reset-toast-${msg.kind}`}
                className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border text-sm ${
                  msg.kind === "success"
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : "bg-red-50 border-red-200 text-red-700"
                }`}
              >
                {msg.kind === "success" ? (
                  <Check className="w-4 h-4 shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                )}
                <span>{msg.text}</span>
              </div>
            )}

            <button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={busy}
              className="w-full py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold transition-colors disabled:opacity-60"
            >
              {busy ? "Sending…" : "Reset Password"}
            </button>
          </form>

          <div className="mt-6 text-sm text-stone-500 text-center">
            Remember your password?{" "}
            <Link
              to="/login"
              data-testid="reset-signin-link"
              className="text-brand-600 hover:text-brand-700 font-semibold"
            >
              Sign in here
            </Link>
          </div>

          <div className="mt-5 flex items-start gap-2 text-xs text-stone-500 leading-relaxed">
            <ShieldAlert className="w-4 h-4 text-warmth-500 shrink-0 mt-0.5" />
            <span>For security reasons, we don't disclose whether an email exists in our system.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
